/**
 * Money is integer cents everywhere — in the database (bigint), on the wire,
 * and in component state. Dollars appear only in two places: a text input a
 * person types into, and a formatted string a person reads. These helpers are
 * the only sanctioned way across that boundary.
 */
/** "$1,234.56"; negative amounts as "-$12.34". */
export function formatCents(cents, options = {}) {
    if (cents == null || Number.isNaN(cents))
        return options.empty ?? '$0.00';
    const abs = Math.abs(Math.trunc(cents));
    const whole = Math.floor(abs / 100).toLocaleString('en-US');
    const frac = String(abs % 100).padStart(2, '0');
    const body = `$${whole}.${frac}`;
    if (cents < 0)
        return `-${body}`;
    return options.sign === 'always' ? `+${body}` : body;
}
/**
 * Parses what a person typed into a dollar field. String arithmetic, never
 * `Math.round(x * 100)`: "0.29" becomes 29 exactly, and "12.345" is refused
 * rather than rounded, because a third decimal in a money field is a typo.
 */
export function parseDollarsToCents(input) {
    if (input == null)
        return null;
    const clean = String(input).replace(/[$,\s]/g, '');
    if (clean === '' || clean === '-' || clean === '.')
        return null;
    if (!/^-?\d*(\.\d{0,2})?$/.test(clean))
        return null;
    const negative = clean.startsWith('-');
    const [dollars = '0', centsPart = ''] = clean.replace('-', '').split('.');
    const cents = Number(dollars || '0') * 100 + Number((centsPart + '00').slice(0, 2));
    if (!Number.isSafeInteger(cents))
        return null;
    return negative ? -cents : cents;
}
/** Cents → the string a dollar input should show ("12.30"). */
export function centsToDollarsInput(cents) {
    if (cents == null)
        return '';
    const abs = Math.abs(cents);
    const s = `${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
    return cents < 0 ? `-${s}` : s;
}
export function sumCents(values) {
    let total = 0;
    for (const value of values)
        total += value ?? 0;
    return total;
}
/**
 * Splits an amount proportionally, with the rounding remainder going to the
 * first share, so the parts always sum to the whole. Used for refunds: a
 * refund of R on a sale with subtotal S and discount D reverses R×S/T of
 * income and R×D/T of discount.
 */
export function splitProportionally(total, weights) {
    const weightSum = weights.reduce((a, b) => a + b, 0);
    if (weightSum === 0)
        return weights.map((_, i) => (i === 0 ? total : 0));
    const parts = weights.map((w) => Math.floor((total * w) / weightSum));
    const remainder = total - parts.reduce((a, b) => a + b, 0);
    parts[0] = (parts[0] ?? 0) + remainder;
    return parts;
}
//# sourceMappingURL=money.js.map